import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { signIn, signUp, signOut, getCurrentUser, confirmSignUp, resetPassword, confirmResetPassword, updatePassword, fetchUserAttributes, fetchAuthSession, resendSignUpCode, sendUserAttributeVerificationCode, confirmUserAttribute } from '@aws-amplify/auth';

export interface AuthUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: 'user' | 'analyst' | 'admin' | 'superuser';
  isEmailVerified: boolean;
}

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export interface SignInData {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  currentUser$ = this.currentUserSubject.asObservable();
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor() {
    this.checkCurrentUser();
  }

  private async checkCurrentUser(): Promise<void> {
    try {
      const cognitoUser = await getCurrentUser();
      const user = await this.mapCognitoUserToAuthUser(cognitoUser);
      this.currentUserSubject.next(user);
    } catch (error) {
      this.currentUserSubject.next(null);
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  private async mapCognitoUserToAuthUser(cognitoUser: any): Promise<AuthUser> {
    try {
      // Use fetchUserAttributes to get the most reliable user data
      const userAttributes = await fetchUserAttributes();
      
      // Get user role using the modern fetchAuthSession approach
      const role = await this.getUserRole();
      
      return {
        userId: cognitoUser.userId || cognitoUser.username,
        email: userAttributes.email || '',
        firstName: userAttributes.given_name || '',
        lastName: userAttributes.family_name || '',
        phone: userAttributes.phone_number || '',
        role: role,
        isEmailVerified: Boolean(userAttributes.email_verified)
      };
    } catch (error) {
      // Fallback to the original method if fetchUserAttributes fails
      const attributes = cognitoUser.userAttributes || cognitoUser.attributes || {};
      const signInUserSession = cognitoUser.signInUserSession;
      const idTokenPayload = signInUserSession?.idToken?.payload || {};
      
      const firstName = attributes.given_name || idTokenPayload.given_name || attributes['custom:given_name'] || '';
      const lastName = attributes.family_name || idTokenPayload.family_name || attributes['custom:family_name'] || '';
      const email = attributes.email || idTokenPayload.email || '';
      const phone = attributes.phone_number || idTokenPayload.phone_number || '';
      
      return {
        userId: cognitoUser.userId || cognitoUser.username,
        email: email,
        firstName: firstName,
        lastName: lastName,
        phone: phone,
        role: await this.getUserRole(),
        isEmailVerified: attributes.email_verified === 'true' || idTokenPayload.email_verified === true
      };
    }
  }

  private async getUserRole(): Promise<'user' | 'analyst' | 'admin' | 'superuser'> {
    try {
      // Use fetchAuthSession to get the ID token with group information
      const session = await fetchAuthSession();
      const idTokenPayload = session.tokens?.idToken?.payload;
      const groupsRaw = idTokenPayload?.['cognito:groups'];
      
      // Ensure groups is an array of strings
      const groups = Array.isArray(groupsRaw) ? groupsRaw as string[] : [];
      
    
      
      if (groups.includes('superusers')) {
        return 'superuser';
      } else if (groups.includes('admins')) {
        return 'admin';
      } else if (groups.includes('analysts')) {
        return 'analyst';
      }
      
      return 'user';
    } catch (error) {
      return 'user';
    }
  }

  signIn(data: SignInData): Observable<AuthUser> {
    return from(this.performSignIn(data));
  }

  private async performSignIn(data: SignInData): Promise<AuthUser> {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: data.email,
        password: data.password
      });

      if (isSignedIn) {
        const cognitoUser = await getCurrentUser();
        const user = await this.mapCognitoUserToAuthUser(cognitoUser);
        this.currentUserSubject.next(user);
        return user;
      } else {
        // Check if the next step is RESET_PASSWORD
        if (nextStep?.signInStep === 'RESET_PASSWORD') {
          throw new Error('RESET_PASSWORD');
        }
        // Check if the next step is CONFIRM_SIGN_UP (unconfirmed user)
        if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          throw new Error('USER_NOT_CONFIRMED');
        }
        throw new Error(`Sign-in requires additional step: ${nextStep?.signInStep}`);
      }
    } catch (error: any) {
      // Pass through special errors as-is for custom handling
      if (error.message === 'RESET_PASSWORD' || error.message === 'USER_NOT_CONFIRMED') {
        throw error;
      }

      // Check for UserNotConfirmedException in the original error
      if (error.name === 'UserNotConfirmedException') {
        throw new Error('USER_NOT_CONFIRMED');
      }

      throw new Error(this.getErrorMessage(error));
    }
  }

  signUp(data: SignUpData): Observable<{ userId: string; nextStep: any }> {
    return from(this.performSignUp(data));
  }

  private async performSignUp(data: SignUpData): Promise<{ userId: string; nextStep: any }> {
    try {
      const { userId, nextStep } = await signUp({
        username: data.email,
        password: data.password,
        options: {
          userAttributes: {
            email: data.email,
            given_name: data.firstName,
            family_name: data.lastName,
            phone_number: data.phone
          }
        }
      });

      return { userId: userId!, nextStep };
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  confirmSignUp(email: string, code: string): Observable<boolean> {
    return from(this.performConfirmSignUp(email, code));
  }

  private async performConfirmSignUp(email: string, code: string): Promise<boolean> {
    try {
      // First try account confirmation (for new signups)
      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode: code
      });

      return isSignUpComplete;
    } catch (error: any) {
      // If that fails with "User is already confirmed", try attribute verification
      if (error.name === 'InvalidParameterException' &&
          error.message?.includes('User is already confirmed')) {
        try {
          await confirmUserAttribute({
            userAttributeKey: 'email',
            confirmationCode: code
          });
          return true; // Email attribute verified successfully
        } catch (attributeError: any) {
          throw new Error(this.getErrorMessage(attributeError));
        }
      }
      throw new Error(this.getErrorMessage(error));
    }
  }

  resendConfirmationCode(email: string): Observable<any> {
    return from(this.performResendConfirmationCode(email));
  }

  private async performResendConfirmationCode(email: string): Promise<any> {
    try {
      // First try account confirmation (for new signups)
      const result = await resendSignUpCode({
        username: email
      });
      return result;
    } catch (error: any) {
      // If that fails with "User is already confirmed", try attribute verification
      if (error.name === 'InvalidParameterException' &&
          error.message?.includes('User is already confirmed')) {
        try {
          const attributeResult = await sendUserAttributeVerificationCode({
            userAttributeKey: 'email'
          });
          return attributeResult;
        } catch (attributeError: any) {
          // Attribute verification requires a signed-in user
          if (attributeError.name === 'UserUnAuthenticatedException') {
            throw new Error('This account is already verified. Use "Forgot password" to set a password and sign in.');
          }
          throw new Error(this.getErrorMessage(attributeError));
        }
      }
      throw new Error(this.getErrorMessage(error));
    }
  }

  signOut(): Observable<void> {
    return from(this.performSignOut());
  }

  private async performSignOut(): Promise<void> {
    try {
      await signOut();
      this.currentUserSubject.next(null);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  resetPassword(email: string): Observable<any> {
    return from(this.performResetPassword(email));
  }

  private async performResetPassword(email: string): Promise<any> {
    try {
      return await resetPassword({ username: email });
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  confirmResetPassword(email: string, code: string, newPassword: string): Observable<void> {
    return from(this.performConfirmResetPassword(email, code, newPassword));
  }

  private async performConfirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  updatePassword(oldPassword: string, newPassword: string): Observable<void> {
    return from(this.performUpdatePassword(oldPassword, newPassword));
  }

  private async performUpdatePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      await updatePassword({
        oldPassword: oldPassword,
        newPassword: newPassword
      });
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  get isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  get currentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  hasRole(role: 'user' | 'analyst' | 'admin' | 'superuser'): boolean {
    const user = this.currentUser;
    if (!user) return false;

    const roleHierarchy = ['user', 'analyst', 'admin', 'superuser'];
    const userRoleLevel = roleHierarchy.indexOf(user.role);
    const requiredRoleLevel = roleHierarchy.indexOf(role);

    return userRoleLevel >= requiredRoleLevel;
  }

  private getErrorMessage(error: any): string {
    if (error.name) {
      switch (error.name) {
        case 'UserNotConfirmedException':
          return 'Please confirm your email address before signing in.';
        case 'NotAuthorizedException':
          return 'Incorrect email or password.';
        case 'UsernameExistsException':
          return 'An account with this email already exists.';
        case 'InvalidParameterException':
          if (error.message && error.message.includes('Cannot reset password for the user as there is no registered/verified email')) {
            return 'Your email address needs to be verified before you can reset your password.';
          }
          return 'Invalid input. Please check your information.';
        case 'CodeMismatchException':
          return 'Invalid verification code.';
        case 'ExpiredCodeException':
          return 'Verification code has expired.';
        case 'LimitExceededException':
          return 'Too many attempts. Please try again later.';
        case 'UserNotFoundException':
          return 'No account found with this email.';
        default:
          return error.message || 'An unexpected error occurred.';
      }
    }
    return error.message || 'An unexpected error occurred.';
  }


  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch (error) {
      return null;
    }
  }
}